import { observer } from 'owa-mobx-react';
import type { AriaProperties } from 'owa-accessibility';
import { AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import {
    getMailAccountSources,
    getGlobalSettingsAccountMailboxInfo,
} from 'owa-account-source-list-store';
import { isMonarchMultipleAccountsEnabled } from 'owa-account-source-list';
import loc, { format } from 'owa-localize';
import {
    favoritesHeaderText,
    favoritesWithAccountNameHeaderText,
} from 'owa-locstrings/lib/strings/favoritesheadertext.locstring.json';
import {
    lazyToggleFavoritesTreeExpansion,
    getMailFavoritesViewState,
} from 'owa-mail-favorites-store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import TreeNode from 'owa-tree-node/lib/components/TreeNode';
import React from 'react';
import FavoritesList from './FavoritesList';
import {
    animationContainer,
    contentContainer,
} from 'owa-expansion-animation/lib/styles/expansionAnimationStyles.scss';
import { transitionStyles } from 'owa-expansion-animation';
import { CSSTransition } from 'react-transition-group';
import type { MailboxInfo } from 'owa-client-types';
import { getAccountKeyForMailboxInfo } from 'owa-client-types';
import { logUsage } from 'owa-analytics';
import {
    setContextMenuStateMutator,
    showFavoritesContextMenu,
} from 'owa-mail-favorites-store/lib/actions/favoritesContextMenu';
import { FavoritesRootContextMenu } from '../lazyFunctions';
import { getAnchorForContextMenu } from 'owa-positioning';
import { type FolderForestNodeType } from 'owa-favorites-types';
import { SourceMap } from 'owa-folders-constants';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';

const FAVORITES_ROOT_NODE_ID = 'favoritesRoot';

export interface FavoritesProps extends React.HTMLProps<HTMLDivElement> {
    style?: React.CSSProperties;
}

const ariaProps: AriaProperties = {
    role: AriaRoles.group,
    labelledBy: FAVORITES_ROOT_NODE_ID,
};

const Favorites: React.FunctionComponent<FavoritesProps> = observer(function Favorites(
    props: FavoritesProps
) {
    const mailboxInfo = getGlobalSettingsAccountMailboxInfo();

    const nodeRef = React.useRef(null);
    const rootNodeRef = React.useRef<HTMLDivElement | null>(null);

    const onClick = React.useCallback(async (evt: React.MouseEvent<unknown> | KeyboardEvent) => {
        evt.stopPropagation();

        lazyToggleFavoritesTreeExpansion.importAndExecute();
    }, []);

    const getSelectedEmailAddress = () => {
        if (getMailAccountSources().length > 0 && isMonarchMultipleAccountsEnabled()) {
            return mailboxInfo.mailboxSmtpAddress;
        }

        return null;
    };
    const selectedEmailAddress = getSelectedEmailAddress();
    const displayName =
        selectedEmailAddress && !isMonarchMultipleAccountsEnabled()
            ? format(loc(favoritesWithAccountNameHeaderText), selectedEmailAddress)
            : loc(favoritesHeaderText);

    const onContextMenu = React.useCallback((evt: React.MouseEvent<HTMLElement>) => {
        evt.stopPropagation();
        evt.preventDefault();

        logUsage('FP_ShowFavoriteRootCtxMenu', mailboxInfo);
        showFavoritesContextMenu(
            'favoritesRoot',
            0, // not used by this context menu
            getAnchorForContextMenu(evt),
            undefined /*folderId*/,
            true /*showRootMenu*/
        );
    }, []);

    const shouldShowTreeExpanded =
        !getUserConfiguration()?.UserOptions?.IsFavoritesFolderTreeCollapsed;

    const chevronProps = React.useMemo(() => {
        return {
            isExpanded: shouldShowTreeExpanded,
            onClick,
            isAnimated: true,
        };
    }, [shouldShowTreeExpanded, onClick]);

    return (
        <div
            style={props.style}
            className={props.className}
            {...generateDomPropertiesForAria(ariaProps)}
        >
            <TreeNode
                elementRef={rootNodeRef}
                id={FAVORITES_ROOT_NODE_ID}
                chevronProps={chevronProps}
                displayName={displayName}
                depth={0}
                isRootNode={true}
                isSelected={false}
                key="favoritesRoot"
                onClick={onClick}
                onContextMenu={onContextMenu}
                ellipsesOnHover={true}
                source={SourceMap.account}
                shouldShowFocusBorder={true}
                mailboxInfo={mailboxInfo}
                useNewSelectionUI={isGlobalFeatureEnabled('fp-newfoldersselection-ui')}
            />
            <FavoritesRootContextMenuWrapper mailboxInfo={mailboxInfo} />

            <CSSTransition
                in={shouldShowTreeExpanded}
                timeout={200}
                unmountOnExit={true}
                classNames={transitionStyles}
                nodeRef={nodeRef}
            >
                <div className={animationContainer} ref={nodeRef}>
                    <div className={contentContainer}>
                        <FavoritesList key="favoriteFolderList" mailboxInfo={mailboxInfo} />
                    </div>
                </div>
            </CSSTransition>
        </div>
    );
},
'Favorites');

export interface FavoritesRootContextMenuWrapperProps {
    mailboxInfo: MailboxInfo;
}
const FavoritesRootContextMenuWrapper = observer(function FavoritesRootContextMenuWrapper(
    props: FavoritesRootContextMenuWrapperProps
) {
    const { mailboxInfo } = props;

    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);

    const onContextMenuDismissed = React.useCallback(() => {
        setContextMenuStateMutator(accountKey, null);
    }, [accountKey]);

    const contextMenuState = getMailFavoritesViewState(accountKey)?.contextMenuState;
    // Render the context menu if it is set to be visible
    if (!contextMenuState || !contextMenuState.showRootMenu) {
        return null;
    }

    return (
        <FavoritesRootContextMenu
            anchorPoint={contextMenuState.anchor}
            onDismiss={onContextMenuDismissed}
            mailboxInfo={props.mailboxInfo}
        />
    );
},
'FavoritesRootContextMenuWrapper');

export default Favorites;
